import { buildRumId } from './lib/buildRumId';
import { buildRumUrl } from './lib/buildRumUrl';
import { parseTraceparent } from './lib/tracing/parseTraceparent';
import { setActiveTraceContext } from './lib/tracing/activeTraceContext';
import { parseServerTimeFromTracestate } from './lib/tracing/parseServerTimeFromTracestate';
import { initServerTimeOffset } from './lib/tracing/serverTimeOffset';

export { setActiveTraceId } from './lib/activeTraceId';
export { setActiveTraceContext } from './lib/tracing/activeTraceContext';
export type { ActiveTraceContext } from './lib/tracing/activeTraceContext';

type RUMSettings = Ya.Rum.RUMSettings;
type RumVars = Ya.Rum.RumVars;

/**
 * Параметры сервиса для инициализации счетчика.
 */
export type InitRumInlineOptions = {
    /**
     * Окружение приложения.
     */
    env: string;
    /**
     * UID пользователя.
     */
    uid: string;
    /**
     * Домен.
     */
    tld: string;
    /**
     * RequestID сессии.
     */
    reqid: string;
    /**
     * А/Б эксперименты из ABT.
     */
    slots: string[];
    /**
     * Экспериментальные флаги для поля exp_flgs.
     */
    experiments?: string[];
    /**
     * Регион пользователя.
     */
    region: string;
    /**
     * Сервис логирования в Rum.
     */
    service?: string;
    /**
     * Проект логирования в Rum.
     */
    project: string;
    /**
     * Платформа: desktop или touch.
     */
    platform: string;
    /**
     * Язык пользователя.
     */
    lang?: string;
};

/**
 * Инициализация инлайновой части Rum счетчика.
 *
 * @kind helper
 * @param params - Параметры сервиса для инициализации счетчика.
 * @returns undefined
 */
export function initRumInline(params: InitRumInlineOptions): void {
    require('@yandex-int/rum-counter/dist/inline/interface');
    require('@yandex-int/rum-counter/dist/inline/io');
    require('@yandex-int/rum-counter/dist/inline/longtask');
    require('@yandex-int/rum-counter/dist/bundle/send');
    require('./lib/deferred-send/inline');

    const { env, uid, tld, reqid, slots, experiments, region, service, project, platform, lang } = params;

    const settings: RUMSettings = {
        beacon: Boolean(navigator.sendBeacon),
        clck: `https://yandex.${tld}/clck/click`,
        reqid,
        slots,
        experiments,
        sendAutoResTiming: true,
        sendUrlInResTiming: true,
        sendCookie: true,
        sendClientUa: true,
        sendFirstRaf: true,
        // Включаем непрерывный сбор метрик CLS и INP в течение всей жизни страницы.
        enableContinuousCollection: true,
        // Игнорируем фоновые события INP
        ignoreBackgroundINPEvents: true,
        // Поле не описано в типах rum-counter. Отключаем отправку FID, т.к. более не смотрим на эту метрику,
        // а также она может отправляться уже после перехода на другую страницу, что ломает логирование.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error -- см. выше
        disableFID: true,
        observeAllInp: true,
    };

    const page = buildRumId({ project, service, platform });
    const url = buildRumUrl();

    const vars: RumVars = {
        rum_id: page,
        region,
        '-env': env,
        '-uid': uid,
        '-page': page,
        '-project': project,
        '-platform': platform,
        // Докидываем URL в '-additional', не используя поле '-url',
        // потому что поле '-url' не прорастает в ClickHouse таблицы.
        '-additional': JSON.stringify({ tld, page, url, lang }),
    };

    if (service) {
        vars['-service'] = service;
    }

    Ya.Rum.init(settings, vars);
}

/**
 * Инициализирует активный trace-контекст из строки traceparent (W3C Trace Context).
 *
 * @kind helper
 * @param traceparent — строка в формате `00-{traceId}-{spanId}-{flags}` из meta-тега или заголовка.
 * При невалидном формате контекст не устанавливается.
 */
export function setActiveTraceparent(traceparent: string): void {
    const ctx = parseTraceparent(traceparent);

    if (ctx !== undefined) {
        setActiveTraceContext(ctx);
    }
}

/**
 * Инициализирует смещение серверного времени из строки tracestate (W3C Trace Context).
 *
 * Извлекает серверное время из vendor-записи `yd=st:<unix_ms>` и вычисляет
 * смещение между серверными и клиентскими часами. Смещение применяется
 * ко всем клиентским спанам при отправке, чтобы они корректно ложились
 * в дерево серверных трейсов.
 *
 * @kind helper
 * @param tracestate — строка в формате W3C Trace Context tracestate из meta-тега.
 * При отсутствии записи `yd=st:*` или невалидном значении смещение не изменяется.
 */
export function setActiveTracestate(tracestate: string): void {
    const serverTime = parseServerTimeFromTracestate(tracestate);

    if (serverTime !== undefined) {
        initServerTimeOffset(serverTime);
    }
}
